import { useLayoutEffect, useState, type RefObject } from 'react';

/**
 * I-1308 (CFC-f6b, `docs/concept-content-focus.md`): the sidebar's footer joins its list when pinned
 * it would leave the list less than half the sidebar. Measured on the iPad (1180 × 820, WebKit) at
 * the largest text size: the footer (the Configuration lines, the account and its icons) took 537
 * of 820, and the list that scrolls above it kept 259 — two lines of it at a time. Then the two are
 * one scroller, and the footer comes after the list's last line; at any other size the footer stays
 * pinned, as I-0395 has it.
 */
export function footerJoinsList(sidebarHeight: number, footerHeight: number): boolean {
  return sidebarHeight > 0 && sidebarHeight - footerHeight < sidebarHeight / 2;
}

/** Whether the footer (`footer`) scrolls with the list in `sidebar`, again whenever either resizes. */
export function useOneSidebarScroller(
  sidebar: RefObject<HTMLElement | null>,
  footer: RefObject<HTMLElement | null>,
): boolean {
  const [joined, setJoined] = useState(false);
  useLayoutEffect(() => {
    const [column, foot] = [sidebar.current, footer.current];
    if (!column || !foot || typeof ResizeObserver === 'undefined') return undefined;
    // The footer's own height, the same whether it is pinned or scrolls: deciding moves nothing it
    // reads, so the choice cannot flip back and forth.
    const read = () => setJoined(footerJoinsList(column.clientHeight, foot.offsetHeight));
    read();
    const observer = new ResizeObserver(read);
    observer.observe(column);
    observer.observe(foot);
    return () => observer.disconnect();
  }, [sidebar, footer]);
  return joined;
}
